import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Table,
} from 'reactstrap';

import { get } from '../../helpers/api_helper';

const sortValues = (left, right) => String(left || '').localeCompare(String(right || ''), undefined, {
  numeric: true,
  sensitivity: 'base',
});

function RecountBanner({ t }) {
  const [locations, setLocations] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [sort, setSort] = useState({ field: 'missing_at', ascending: true });

  useEffect(() => {
    let mounted = true;

    const loadLocations = async () => {
      try {
        const data = await get('/api/warehouse/location/recount');
        if (mounted) setLocations(Array.isArray(data) ? data : []);
      } catch (error) {
        if (mounted) setLocations(null);
      }
    };

    loadLocations();
    return () => { mounted = false; };
  }, []);

  const missingToday = useMemo(() => {
    if (!locations) return 0;
    const oneDayAgo = moment().subtract(1, 'day');
    return locations.filter((location) => (
      location.missing_at && moment(location.missing_at).isAfter(oneDayAgo)
    )).length;
  }, [locations]);

  const sortedLocations = useMemo(() => {
    if (!locations) return [];
    const direction = sort.ascending ? 1 : -1;
    return [...locations].sort((left, right) => (
      sortValues(left[sort.field], right[sort.field]) * direction
    ));
  }, [locations, sort]);

  const changeSort = (field) => {
    setSort((current) => ({
      field,
      ascending: current.field === field ? !current.ascending : true,
    }));
  };

  const sortIndicator = (field) => {
    if (sort.field !== field) return '↕';
    return sort.ascending ? '↑' : '↓';
  };

  const displayDate = (value) => (
    value ? moment(value).format('M/D/YYYY h:mm A') : t('Never')
  );

  if (locations === null || missingToday === 0) return null;

  return (
    <>
      <Alert
        color="warning"
        className="dashboard-recount-banner"
        role="button"
        tabIndex="0"
        onClick={() => setModalOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') setModalOpen(true);
        }}
      >
        <i className="bx bx-error-circle" aria-hidden="true" />
        <span>
          {t('{{count}} {{locations}} marked missing today. Check {{them}} with Count -> Guide Me on the scanner', {
            count: missingToday,
            locations: missingToday === 1 ? t('location') : t('locations'),
            them: missingToday === 1 ? t('it') : t('them'),
          })}
        </span>
        <i className="bx bx-chevron-right dashboard-recount-banner__arrow" aria-hidden="true" />
      </Alert>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setModalOpen(false)}>{t('Locations to count')}</ModalHeader>
        <ModalBody>
          {sortedLocations.length === 0 ? (
            <p className="mb-0 text-muted">{t('No locations need counting')}</p>
          ) : (
            <div className="table-responsive">
              <Table className="dashboard-recount-table mb-0" hover>
                <thead>
                  <tr>
                    <th>
                      <Button color="link" onClick={() => changeSort('label')}>
                        {`${t('Location')} ${sortIndicator('label')}`}
                      </Button>
                    </th>
                    <th>
                      <Button color="link" onClick={() => changeSort('counted_at')}>
                        {`${t('Counted')} ${sortIndicator('counted_at')}`}
                      </Button>
                    </th>
                    <th>
                      <Button color="link" onClick={() => changeSort('missing_at')}>
                        {`${t('Missing')} ${sortIndicator('missing_at')}`}
                      </Button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLocations.map((location) => (
                    <tr key={location.id}>
                      <td><strong>{location.label}</strong></td>
                      <td>{displayDate(location.counted_at)}</td>
                      <td>{displayDate(location.missing_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalOpen(false)}>{t('Close')}</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

RecountBanner.propTypes = {
  t: PropTypes.func.isRequired,
};

export default RecountBanner;
